import type {
  VoiceNavPendingOpenModalityWorkflow,
  VoiceNavSessionActivePatientPayload,
} from '../api/apiConfig'
import type { ActivePatientSyncPayload } from '../domain/patientHost/types'
import type { ChatMessage } from '../types/chat'
import { patientNamesLikelyEqual } from './patientSwitchConfirm'

/**
 * After a full reload, the copilot may still have ``pending_action`` (awaiting switch Yes) while the
 * host app already shows the requested chart. Any embed can pass that chart via ``patientContext``;
 * the server session may also already list the same patient on ``GET /session/context``.
 */
export function hostOrSessionChartMatchesModalitySwitchTarget(input: {
  workflow: VoiceNavPendingOpenModalityWorkflow
  hostPatient: ActivePatientSyncPayload | null
  sessionActivePatient: VoiceNavSessionActivePatientPayload | null | undefined
}): boolean {
  return modalitiesQueuedSwitchChartMatches({
    workflow: input.workflow,
    hostPatient: input.hostPatient,
    sessionActivePatient: input.sessionActivePatient,
    switchRequestedPatientName: null,
  })
}

/**
 * Same as ``hostOrSessionChartMatchesModalitySwitchTarget`` but also compares the patient-switch
 * card's ``requestedPatientName`` (host may line up with the card before Redis ``active_patient`` catches up).
 */
export function modalitiesQueuedSwitchChartMatches(input: {
  workflow: VoiceNavPendingOpenModalityWorkflow
  hostPatient: ActivePatientSyncPayload | null
  sessionActivePatient: VoiceNavSessionActivePatientPayload | null | undefined
  switchRequestedPatientName?: string | null
}): boolean {
  if (
    input.workflow.kind !== 'open_modality_for_patient' &&
    input.workflow.kind !== 'view_prescription_for_patient' &&
    input.workflow.kind !== 'sports_noc_for_patient' &&
    input.workflow.kind !== 'renew_ordonnance_for_patient'
  ) {
    return false
  }
  const targets = new Set<string>()
  const t0 = (input.workflow.target_patient_name || '').trim()
  if (t0) targets.add(t0)
  const t1 = (input.switchRequestedPatientName || '').trim()
  if (t1) targets.add(t1)
  if (targets.size === 0) return false

  const chartNames: string[] = []
  const hn = (input.hostPatient?.patient_name || '').trim()
  if (hn) chartNames.push(hn)
  const sn = (input.sessionActivePatient?.patient_name || '').trim()
  if (sn) chartNames.push(sn)

  for (const chart of chartNames) {
    for (const tgt of targets) {
      if (patientNamesLikelyEqual(chart, tgt)) return true
    }
  }

  return false
}

export function pendingSwitchRequestedNameFromMessages(
  messages: readonly ChatMessage[],
  pendingSwitchMessageId: string,
): string | null {
  const m = messages.find((x) => x.id === pendingSwitchMessageId)
  const rq = (m?.patientSwitchConfirm?.requestedPatientName || '').trim()
  return rq || null
}
